import React from 'react';
import { useEffectOnce } from 'react-use';
// eslint-disable-next-line no-restricted-imports -- 기존 사용처. overlay-kit 마이그레이션은 별건으로 진행한다.
import { useModal } from '@ebay/nice-modal-react';
import { withAsyncBoundary } from '@toss/async-boundary';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { match } from 'ts-pattern';
import { logger } from '@zep/core-logger';
import { RotateCircleIcon } from '@zep/icons';
import { Spacing, Stack } from '@zep/ui';
import { Colors } from '@zep/ui-tokens';
import { cn, getResizedImage } from '@zep/utils';
import { usePlanDetail } from '../../../../services';
import { SubscribePlan } from '../../../../types';
import { Vector1 } from '../../shared';
import { PaymentUpdateStatusType, PhoneNumberInputDialog, PlanApplyFeature } from '../../subscription';
import { PlanIcon } from '../shared/PlanIcon';
import { PlanTemplateType, SubscriptionPeriod } from '../types';
import { derivePlanCompleteView } from './derivePlanCompleteView';
import { ConfettiExplosionWrapper, SuccessActionButtons } from './shared';
const PlanPaymentSuccessContent = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    query
  } = useRouter();
  const spacePlanHashId = (query.spacePlanHashId || '').toString();
  const updateStatus = (query.updateStatus || '') as PaymentUpdateStatusType;
  const {
    data: planDetailData
  } = usePlanDetail(spacePlanHashId);
  const spaceHashId = planDetailData?.space?.hashId || '';
  const entryMapHashId = planDetailData?.space?.entryMapHashId || '';
  const title = updateStatus === 'DOWN_GRADE' || updateStatus === 'UP_GRADE' ? t('subscription:payment.complete.downgradeTitle', '플랜 변경 완료') : t('subscription:payment.complete.defaultTitle', '플랜 결제 완료');
  // 완료 화면 표시값은 순수 함수로 파생한다. 다운그레이드는 racy한 서버 필드
  // (nextSpacePlanTemplate) 대신 완료 URL 쿼리를 우선 사용해 전파 지연 race를
  // 원천 차단한다(가드/폴백은 그쪽에서 검증).
  const {
    plan,
    maxCcu,
    subscriptionPeriod
  } = derivePlanCompleteView(planDetailData, updateStatus, {
    planType: typeof query.planType === 'string' ? query.planType : undefined,
    maxCcu: typeof query.maxCcu === 'string' ? query.maxCcu : undefined,
    subscriptionPeriod: typeof query.subscriptionPeriod === 'string' ? query.subscriptionPeriod : undefined
  });
  const thumbnailUrl = planDetailData?.space?.thumbnailUrl || '';
  const spaceName = planDetailData?.space?.name || '';
  const orderId = planDetailData?.paymentInfo?.orderId || '';
  const isJaPlan = planDetailData?.space?.countryCode === 'JP';
  const phoneNumberInputModal = useModal(PhoneNumberInputDialog);
  useEffectOnce(() => {
    phoneNumberInputModal.show({
      spaceHashId: String(spaceHashId),
      planTemplateType: PlanTemplateType.PRODUCT_SUBSCRIPTION
    });
  });

  // GTM purchase 신호는 이 페이지가 아니라 usePlanSubscriptionAction의
  // 서버 성공 응답 시점에서 push한다. 이 플로우는 신규 첫 결제도
  // updateStatus(UP_GRADE)가 붙어 페이지 레벨에서는 신규/변경 구분과
  // 실결제액 판별이 불가능하기 때문이다.

  return <ConfettiExplosionWrapper triggerOnMount={updateStatus !== 'DOWN_GRADE'} particleSize={8} data-sentry-element="ConfettiExplosionWrapper" data-sentry-component="PlanPaymentSuccessContent" data-sentry-source-file="PlanPaymentSuccess.tsx">
      <div className="flex h-[calc(100vh-64px)] items-center px-8">
        <div className="user-select-none mx-auto flex min-h-[470px] w-full max-w-[716px] flex-col rounded-[10px] bg-white shadow-[0_2px_10px_0_rgba(42,39,65,0.10)]">
          <div className="flex-1 px-[40px] pb-[20px] pt-[30px]">
            <h1 className="text-h5 font-[700] text-gray-900">{title}</h1>
            <div className="flex h-[calc(100%-30px)] flex-col pt-[24px] md:flex-row">
              <section className="flex flex-col gap-3 pr-[20px]">
                <SubscriptionSpaceInfo maxCcu={maxCcu} plan={plan} thumbnailUrl={thumbnailUrl} spaceName={spaceName} subscriptionPeriod={subscriptionPeriod} data-sentry-element="SubscriptionSpaceInfo" data-sentry-source-file="PlanPaymentSuccess.tsx" />
              </section>
              <section className="w-full min-w-[300px] pl-[20px]">
                {orderId && <div className="text-body-2-m font-[500] text-gray-500">
                    {t('subscription:payment.complete.orderNumber')} {orderId}
                  </div>}
                <Spacing size={20} data-sentry-element="Spacing" data-sentry-source-file="PlanPaymentSuccess.tsx" />
                <div className="flex flex-col">
                  {match(updateStatus).with('DOWN_GRADE', () => <>
                        <div className="text-subtitle-sb font-[600] text-gray-900">
                          {t('subscription:payment.complete.planChanged', '플랜 변경이 신청되었습니다.')}
                        </div>
                        <Spacing size={8} />
                        <div className="text-caption-1 font-[400] text-gray-600">
                          {t('subscription:payment.complete.dateNoticeText', {
                      defaultValues: '{{date}}부터 {{spaceName}} 스페이스 {{plan}} {{maxCcu}} 플랜으로 변경되며, 동시에 변경될 플랜의 가격으로 결제가 요청됩니다.',
                      date: format(new Date(planDetailData?.spacePlan?.endDate), 'yyyy. MM. dd'),
                      spaceName,
                      plan: plan.toUpperCase(),
                      maxCcu
                    })}
                        </div>
                      </>).otherwise(() => <>
                        <div className="text-subtitle-sb font-[600] text-gray-900">
                          {t('subscription:payment.complete.normalNotice1', '결제가 정상적으로 완료되었습니다.')}
                        </div>
                        <Spacing size={8} />
                        <div className="text-caption-1 font-[400] text-gray-600">
                          {t('subscription:payment.complete.description', {
                      defaultValues: '{{spaceName}} 스페이스가 {{plan}} {{ccu}} 플랜으로 업그레이드되었습니다.',
                      spaceName,
                      plan: plan.toUpperCase(),
                      ccu: maxCcu
                    })}
                        </div>
                      </>)}
                  <Spacing size={20} data-sentry-element="Spacing" data-sentry-source-file="PlanPaymentSuccess.tsx" />
                  <PlanApplyFeature title={Number(maxCcu) < 0 ? t('subscription:SpaceDetail.menuManage.currentPlanMaxCcu') : t('subscription:SpaceDetail.menuManage.currentPlanCcu', {
                  ccu: maxCcu
                })} plan={plan} isDowngrade={updateStatus === 'DOWN_GRADE'} isJaPlan={isJaPlan} data-sentry-element="PlanApplyFeature" data-sentry-source-file="PlanPaymentSuccess.tsx" />
                </div>
              </section>
            </div>
          </div>
          <div className="flex w-full justify-end px-[40px] pb-[30px] pt-[20px]">
            <SuccessActionButtons spaceHashId={spaceHashId} entryMapHashId={entryMapHashId} data-sentry-element="SuccessActionButtons" data-sentry-source-file="PlanPaymentSuccess.tsx" />
          </div>
        </div>
      </div>
      <style global jsx>{`
        body {
          background-color: #f7fafc;
        }
      `}</style>
    </ConfettiExplosionWrapper>;
};
const SubscriptionSpaceInfo = ({
  thumbnailUrl,
  plan,
  spaceName,
  maxCcu,
  subscriptionPeriod
}: {
  thumbnailUrl: string;
  plan: SubscribePlan;
  spaceName: string;
  maxCcu: string;
  subscriptionPeriod: SubscriptionPeriod;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  return <>
      <div className="h-[140px] w-[250px] overflow-hidden rounded-[6px] shadow-inner md:w-[216px]">
        <img src={getResizedImage(thumbnailUrl, 250)} alt="thumbnail" className="size-full object-cover" />
      </div>
      <Stack gap={6} data-sentry-element="Stack" data-sentry-source-file="PlanPaymentSuccess.tsx">
        <PlanTypeWithMaxCcu planType={plan} maxCcu={+maxCcu} data-sentry-element="PlanTypeWithMaxCcu" data-sentry-source-file="PlanPaymentSuccess.tsx" />
        <div className="flex items-center gap-[6px]">
          <RotateCircleIcon width={16} height={16} fill={Colors.Gray700} data-sentry-element="RotateCircleIcon" data-sentry-source-file="PlanPaymentSuccess.tsx" />
          <span className="text-body-1-sb font-semibold text-gray-900">
            {t('subscription:payment.summarySubscriptionUp.subscriptionPeriodAutoPay', {
            defaultValues: '{{subscriptionPeriod}}마다 자동 결제',
            subscriptionPeriod
          })}
          </span>
        </div>
        <div className="text-body-1 font-[500]">{spaceName}</div>
      </Stack>
    </>;
};
const PlanTypeWithMaxCcu = (props: {
  planType: SubscribePlan;
  maxCcu: number;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  return <div className="flex items-center gap-[6px]" data-sentry-component="PlanTypeWithMaxCcu" data-sentry-source-file="PlanPaymentSuccess.tsx">
      {match(props.planType).with('basic', () => <PlanIcon className="rounded-[5px] bg-gradient-to-r from-[#24B6EC] to-[#1DCFAF]" iconClassName="size-[12px]" />).with('pro', () => <PlanIcon className="rounded-[5px] bg-gradient-to-r from-[#588AFF] to-[#A572FF]" iconClassName="size-[12px]" />).otherwise(() => null)}
      <span className={cn('text-[15px] font-[700]', {
      'text-primary': props.planType === 'pro',
      'text-mintDark': props.planType === 'basic'
    })}>
        {props.planType.toUpperCase()}
      </span>
      <Vector1 data-sentry-element="Vector1" data-sentry-source-file="PlanPaymentSuccess.tsx" />
      <span className={cn('text-body-1 font-[600]', {
      'text-primary': props.planType === 'pro',
      'text-mintDark': props.planType === 'basic'
    })}>
        {t('subscription:payment.complete.MaxCcuPerson', {
        ccu: props.maxCcu,
        defaultValues: `최대 {{ccu}}명`
      })}
      </span>
    </div>;
};
export const PlanPaymentSuccess = withAsyncBoundary(PlanPaymentSuccessContent, {
  pendingFallback: null,
  // 결제는 성공했는데 완료 화면 렌더가 실패한 상황은 조용히 넘기면 안 된다.
  // rejectedFallback은 화면(null)만 담당하고, 실제 예외는 onError에서
  // Sentry로 리포트해 알림이 뜨게 한다. (이전엔 rejectedFallback: () => null이
  // 예외를 통째로 삼켜 Sentry에 아무것도 안 올라오고 빈 화면만 노출됐다)
  rejectedFallback: _ => null,
  onError: error => {
    logger.captureError(error, {
      tags: {
        source: 'PlanPaymentSuccess'
      }
    });
  }
});